import { useState } from "react";
import { Link, useLocation } from "wouter";
import {
  Home,
  Plus,
  Menu,
  BookOpen,
  Heart,
  ShieldAlert,
  LifeBuoy,
  Briefcase,
  Megaphone,
  MessageCircle,
} from "lucide-react";
import { useSidebar } from "@/components/ui/sidebar";
import { useSponsorHomeDefault } from "@/hooks/useSponsorHomeDefault";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";

/**
 * Custom stairwell icon (lucide has no "stairs" glyph). Drawn in the lucide
 * stroke style so it sits consistently next to the other icons.
 */
function StairsIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M3 20h4v-4h4v-4h4v-4h4V4" />
    </svg>
  );
}

/**
 * Minimal "people standing in a circle" mark for the community portal: a
 * connecting ring with four stylized heads around it.
 */
function CommunityIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="7" />
      <circle cx="12" cy="5" r="1.6" fill="currentColor" stroke="none" />
      <circle cx="19" cy="12" r="1.6" fill="currentColor" stroke="none" />
      <circle cx="12" cy="19" r="1.6" fill="currentColor" stroke="none" />
      <circle cx="5" cy="12" r="1.6" fill="currentColor" stroke="none" />
    </svg>
  );
}

interface BottomNavProps {
  userRole: "sponsor" | "sponsee";
}

interface CreateAction {
  label: string;
  description: string;
  href: string;
  icon: typeof BookOpen;
  testid: string;
}

const createActions: CreateAction[] = [
  {
    label: "Journal entry",
    description: "Write a new daily entry",
    href: "/journal",
    icon: BookOpen,
    testid: "create-journal",
  },
  {
    label: "Gratitude list",
    description: "Start a new gratitude list",
    href: "/gratitude-lists",
    icon: Heart,
    testid: "create-gratitude",
  },
  {
    label: "Log a craving",
    description: "Record a craving or trigger",
    href: "/triggers-cravings",
    icon: ShieldAlert,
    testid: "create-craving",
  },
  {
    label: "Crisis plan",
    description: "Build a new crisis plan",
    href: "/triggers-cravings",
    icon: LifeBuoy,
    testid: "create-crisis-plan",
  },
  {
    label: "Service commitment",
    description: "Add a service position",
    href: "/service",
    icon: Briefcase,
    testid: "create-service",
  },
  {
    label: "Post a holla",
    description: "Share with your community",
    href: "/community-portal",
    icon: Megaphone,
    testid: "create-holla",
  },
  {
    label: "Send a message",
    description: "Message your sponsor or sponsee",
    href: "/messages",
    icon: MessageCircle,
    testid: "create-message",
  },
];

export default function BottomNav({ userRole }: BottomNavProps) {
  const [location, setLocation] = useLocation();
  const { setOpenMobile } = useSidebar();
  const [createOpen, setCreateOpen] = useState(false);

  const stepHref = userRole === "sponsor" ? "/sponsor-portal" : "/steps";
  const stepActive =
    userRole === "sponsor"
      ? location.startsWith("/sponsor-portal")
      : location === "/steps" || location.startsWith("/steps/");
  // Task #282 — the Home button targets the Sponsor Portal only when the
  // sponsor opted in via "Show Home as Sponsor Dashboard" (default OFF).
  const { homeHref } = useSponsorHomeDefault();
  const homeActive = location === homeHref;
  const communityActive =
    location === "/community-portal" || location.startsWith("/community-portal/");

  const itemBase =
    "flex flex-col items-center justify-center gap-0.5 rounded-md py-2 hover-elevate active-elevate-2";
  const labelClass = "text-[10px] leading-none";

  const goCreate = (href: string) => {
    setCreateOpen(false);
    setLocation(href);
  };

  return (
    <>
      <nav
        className="fixed inset-x-0 bottom-0 z-50 border-t bg-background md:hidden"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
        data-testid="bottom-nav"
      >
        <div className="grid grid-cols-5 items-end px-1 pt-2">
          {/* 1. Menu (opens the full sidebar) */}
          <button
            type="button"
            onClick={() => setOpenMobile(true)}
            className={`${itemBase} text-muted-foreground`}
            aria-label="Open menu"
            data-testid="button-bottomnav-menu"
          >
            <Menu className="h-5 w-5" />
            <span className={labelClass}>Menu</span>
          </button>

          {/* 2. Step work (role-aware) */}
          <Link
            href={stepHref}
            className={`${itemBase} ${stepActive ? "text-primary" : "text-muted-foreground"}`}
            data-testid="link-bottomnav-stepwork"
          >
            <StairsIcon className="h-5 w-5" />
            <span className={labelClass}>
              {userRole === "sponsor" ? "Sponsor" : "Steps"}
            </span>
          </Link>

          {/* 3. Home (center, emphasized) */}
          <div className="flex flex-col items-center justify-end gap-0.5">
            <Link
              href={homeHref}
              className={`-mt-5 flex h-12 w-12 items-center justify-center rounded-full shadow-md active-elevate-2 ${
                homeActive
                  ? "bg-primary text-primary-foreground"
                  : "bg-primary/90 text-primary-foreground"
              }`}
              aria-label="Home"
              data-testid="link-bottomnav-home"
            >
              <Home className="h-6 w-6" />
            </Link>
            <span
              className={`${labelClass} ${homeActive ? "text-primary" : "text-muted-foreground"}`}
            >
              Home
            </span>
          </div>

          {/* 4. Community portal */}
          <Link
            href="/community-portal"
            className={`${itemBase} ${communityActive ? "text-primary" : "text-muted-foreground"}`}
            data-testid="link-bottomnav-community"
          >
            <CommunityIcon className="h-5 w-5" />
            <span className={labelClass}>Community</span>
          </Link>

          {/* 5. Create (variable creative act) */}
          <button
            type="button"
            onClick={() => setCreateOpen(true)}
            className={`${itemBase} text-muted-foreground`}
            aria-label="Create something new"
            data-testid="button-bottomnav-create"
          >
            <Plus className="h-5 w-5" />
            <span className={labelClass}>Create</span>
          </button>
        </div>
      </nav>

      <Sheet open={createOpen} onOpenChange={setCreateOpen}>
        <SheetContent side="bottom" className="md:hidden">
          <SheetHeader className="text-left">
            <SheetTitle>Create something new</SheetTitle>
            <SheetDescription>Pick what you'd like to start.</SheetDescription>
          </SheetHeader>
          <div className="mt-4 grid gap-1 pb-2">
            {createActions.map((action) => (
              <button
                key={action.testid}
                type="button"
                onClick={() => goCreate(action.href)}
                className="flex items-center gap-3 rounded-md p-3 text-left hover-elevate active-elevate-2"
                data-testid={`button-${action.testid}`}
              >
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-muted text-foreground">
                  <action.icon className="h-4 w-4" />
                </span>
                <span className="min-w-0">
                  <span className="block text-sm font-medium">{action.label}</span>
                  <span className="block text-xs text-muted-foreground">
                    {action.description}
                  </span>
                </span>
              </button>
            ))}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
